// Which of the optional visual effects are switched on.
//
// Every one of these costs something — a shader branch, an extra pass, a wider
// vertex — so they are opt-in and independently switchable rather than a single
// "fancy" flag. The renderer reads this directly each frame; nothing here is
// per-object, so a change is a uniform write at most.
//
// Kept in localStorage so a choice survives the reload the renderer toggle does.

const STORAGE_KEY = "borgEffects";

export const EFFECTS = [
  {
    key: "trafficPulses",
    label: "Traffic pulses",
    hint: "light travelling along the connection lines as work flows",
  },
  {
    key: "particles",
    label: "Fire & smoke",
    hint: "GPU particles from roles logging warnings and errors",
  },
  {
    key: "motionStretch",
    label: "Spinner overshoot",
    hint: "spinners swing past and settle back as they advance",
  },
  {
    key: "depthOfField",
    label: "Depth of field",
    hint: "blur everything outside the locked target",
  },
];

// All on unless a stored choice says otherwise. Each costs a bounded, measured
// amount; the map is a wall display first, so it should look its best without
// anyone having to go and switch it on.
export const effects = {
  trafficPulses: true,
  particles: true,
  motionStretch: true,
  depthOfField: true,
};

// Where the current target is on screen, in view pixels about the middle of the
// viewport, and how big it is. Null when nothing is hovered or locked.
//
// Held here rather than read from the crosshair, because the renderer would then
// have to import it: the crosshair already reaches the renderer through the
// overlay, and closing that loop reorders module evaluation enough to leave other
// modules' imports undefined while they are still initialising. This file imports
// nothing, so nothing can be disturbed by depending on it.
let focus = null;

export function setFocus(next) {
  focus = next;
}

export function lockedFocus() {
  return focus;
}

const listeners = [];

export function onEffectsChanged(fn) {
  listeners.push(fn);
  return () => {
    const at = listeners.indexOf(fn);
    if (at >= 0) listeners.splice(at, 1);
  };
}

export function setEffect(key, on) {
  if (!(key in effects)) return;
  effects[key] = !!on;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(effects));
  } catch (e) {
    // Private browsing or storage disabled; the choice just will not persist.
  }

  for (let i = 0; i < listeners.length; i++) listeners[i](key, effects[key]);
}

function restore() {
  let stored;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return;
  }
  if (!stored) return;

  try {
    const saved = JSON.parse(stored);
    for (const key in effects) {
      if (typeof saved[key] === "boolean") effects[key] = saved[key];
    }
  } catch (e) {
    // Malformed; leave the defaults alone.
  }
}

if (typeof window !== "undefined") {
  restore();
  window.mapEffects = effects;
}
